<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { inject, onMounted, ref, watch } from 'vue';
import { useInfinite } from '@/composables/use-infinity';

const backendApi = inject('backendApi');

const props = defineProps({
    hideLabel: {
        type: Boolean,
        default: false
    },
    itemTitle: {
        default: 'name'
    },
    itemValue: {
        default: 'id'
    },
    args: {
        type: Object,
        default: null
    }
});

const emit = defineEmits(['update']);

const selected = ref(null);
const previousAgencyId = ref(null);

const fetchServices = ({ pattern, page, limit }) =>
    backendApi()
        .services()
        .index({
            pattern,
            agency: props.args?.agency?.id,
            active: true,
            excludeIds: Array.isArray(props.args?.listCharges)
                ? props.args.listCharges.map((c) => c.id)
                : null,
            page,
            limit
        });

const {
    items: services,
    loading: isLoading,
    showRetryButton,
    hasMore,
    searchQuery,
    getItems,
    loadMore,
    reset
} = useInfinite(fetchServices);

const handleSearch = () => {
    reset();
    getItems();
};

// Watch for changes to the agency ID specifically
watch(
    () => props.args?.agency?.id,
    (newAgencyId) => {
        // Only refresh if the agency ID has actually changed
        if (newAgencyId !== previousAgencyId.value) {
            previousAgencyId.value = newAgencyId;
            selected.value = null;
            emit('update', null); // Clear the selected value in parent
            handleSearch(); // Force a fresh fetch when agency changes
        }
    },
    {
        immediate: true
    }
);

// Initial data fetch on mount
onMounted(() => {
    getItems();
});
</script>

<template>
    <AutocompleteFilter
        v-model="selected"
        v-model:search="searchQuery"
        :item-title="itemTitle"
        :item-value="itemValue"
        :items="services"
        :label="hideLabel ? '' : $t('Service')"
        :loading="isLoading"
        :show-retry-button="showRetryButton"
        :infinite-scroll="hasMore"
        :clearable="false"
        @infinite-scroll="loadMore"
        @update="emit('update', selected)"
        @preparing-search="handleSearch"
        @search="handleSearch"
        @retry="handleSearch"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="`${item.raw.name}`"
                :subtitle="`${item.raw.description || ''} ${
                    item.raw.price ? '- ' + item.raw.measure : ''
                }`"
            >
            </v-list-item>
        </template>

        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
    </AutocompleteFilter>
</template>
